import React from 'react';
import Image from '@/components/ui/Image';
import { cn } from '@/lib/utils';
import { SelectableImageRatios } from '@/types/content';

interface ImageProps {
  image?: string;
  altText?: string;
  imageRatio?: SelectableImageRatios;
  imageSize?: "50%" | "75%" | "100%";
  imageAlign?: string;
  linkUrl?: string;
  caption?: string;
  captionAlign?: string;
  hasMobileImage?: boolean;
  mobileImage?: string;
  mobileImageAltText?: string;
  attributes?: React.HTMLProps<HTMLDivElement>;
}

export const ImageBlock = ({ image, altText, linkUrl, imageRatio, imageSize, imageAlign, caption, captionAlign, hasMobileImage, mobileImage, mobileImageAltText, attributes }: ImageProps) => {
  const { key, ...rest } = attributes ?? {};
  const isOpenRatio = imageRatio === 'open' || !imageRatio;
  const aspectRatio = isOpenRatio ? undefined : imageRatio;

  const sectionClass = cn(
    "content-image relative pb-6 flex flex-col",
    imageAlign === 'left' && 'items-start',
    imageAlign === 'center' && 'items-center',
    imageAlign === 'right' && 'items-end',
  );

  const imageClass = cn(
    'w-full rounded-lg',
    imageSize === '50%' && 'w-[50%]',
    imageSize === '75%' && 'w-[75%]',
    imageSize === '100%' && 'w-[100%]',
  );

  const imageStyle = aspectRatio ? { aspectRatio } : {};

  const captionClass = cn(
    "paragraph-sm italic mt-1 text-muted-foreground",
    imageSize === '50%' && 'w-[50%]',
    imageSize === '75%' && 'w-[75%]',
    imageSize === '100%' && 'w-[100%]',
    captionAlign === 'left' && 'text-left',
    captionAlign === 'center' && 'text-center',
    captionAlign === 'right' && 'text-right',
  );

  const showMobileImage = hasMobileImage && mobileImage;

  const renderImage = (src: string, alt: string, extraClass?: string) => (
    <Image
      src={src}
      alt={alt}
      title={alt}
      width={800}
      height={450}
      className={cn(imageClass, extraClass)}
      style={imageStyle}
    />
  );

  return (
    <div
      key={key}
      {...rest}
      className={sectionClass}
    >
      {image ? (
        <>
          {linkUrl ? (
            <a href={linkUrl} target="_blank" rel="noopener noreferrer">
              {showMobileImage ? (
                <>
                  {renderImage(image, altText || '', 'hidden md:block')}
                  {renderImage(mobileImage, mobileImageAltText || altText || '', 'block md:hidden')}
                </>
              ) : (
                renderImage(image, altText || '')
              )}
            </a>
          ) : (
            showMobileImage ? (
              <>
                {renderImage(image, altText || '', 'hidden md:block')}
                {renderImage(mobileImage, mobileImageAltText || altText || '', 'block md:hidden')}
              </>
            ) : (
              renderImage(image, altText || '')
            )
          )}
          {caption && (
            <span className={captionClass}>
              {caption}
            </span>
          )}
        </>
      ) : (
        <>start an image</>
      )}
    </div>
  );
};
